import {
  alpha,
  Box,
  ButtonBase,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef } from "react";
import LocationInfoChip from "@/components/chips/LocationInfoChip";
import { DashboardProduct, DashboardProductType } from "./useDashboardProducts";

/**
 * Browsers only pan a scroll container on touch drags, so when the tab row
 * overflows, mouse users get drag-to-scroll here. A 5px threshold keeps plain
 * clicks selecting tabs; once a drag starts, the resulting click is swallowed.
 */
function useMouseDragScroll() {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef({
    pointerId: -1,
    startX: 0,
    startScrollLeft: 0,
    dragged: false,
  });

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (e.pointerType !== "mouse" || e.button !== 0 || !el) return;
    if (el.scrollWidth <= el.clientWidth) return;
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      dragged: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    const d = drag.current;
    if (!el || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.dragged) {
      if (Math.abs(dx) < 5) return;
      d.dragged = true;
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = d.startScrollLeft - dx;
  };

  const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current.pointerId === e.pointerId) drag.current.pointerId = -1;
  };

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drag.current.dragged) return;
    drag.current.dragged = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return {
    ref: scrollerRef,
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    onClickCapture,
  };
}

/**
 * When the row overflows (tablet / mobile), scrolls the selected tab into
 * view horizontally — so the preselected product is visible on load without
 * nudging the page vertically the way `scrollIntoView` would.
 */
function useScrollSelectedTabIntoView(
  scrollerRef: React.RefObject<HTMLDivElement | null>,
  selectedType: DashboardProductType | undefined
) {
  useEffect(() => {
    const scroller = scrollerRef.current;
    const tab = scroller?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!scroller || !tab) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const overflowLeft = tabRect.left - scrollerRect.left;
    const overflowRight = tabRect.right - scrollerRect.right;
    if (overflowLeft < 0) scroller.scrollLeft += overflowLeft;
    else if (overflowRight > 0) scroller.scrollLeft += overflowRight;
  }, [scrollerRef, selectedType]);
}

function ProductTabStatus({ product }: { product: DashboardProduct }) {
  if (product.status === "inactive") {
    return <LocationInfoChip variant="muted">INACTIVE</LocationInfoChip>;
  }

  const isPaused = product.plan?.status === "paused";
  if (product.status === "cancelled" || isPaused) {
    return (
      <LocationInfoChip variant="warning">
        {isPaused ? "PAUSED" : "CANCELED"}
      </LocationInfoChip>
    );
  }

  return <LocationInfoChip>ACTIVE</LocationInfoChip>;
}

/**
 * The dashboard product tab row (r-1776). Tabs stretch to share the row
 * equally; once they can't fit at their 190px minimum the row scrolls
 * horizontally — they never wrap.
 */
export default function ProductTabs({
  products,
  selectedType,
  onSelect,
}: {
  products: DashboardProduct[];
  selectedType: DashboardProductType | undefined;
  onSelect: (type: DashboardProductType) => void;
}) {
  const dragScroll = useMouseDragScroll();
  useScrollSelectedTabIntoView(dragScroll.ref, selectedType);

  return (
    // The scroller is a transparent wrapper so the scrollbar renders below the
    // white container instead of inside it; the container grows past the
    // viewport and scrolls as one piece with its tabs. Styling the scrollbar
    // opts out of macOS overlay scrollbars, which would otherwise draw on top
    // of the container's bottom edge instead of below it.
    <Box
      {...dragScroll}
      sx={{
        width: "100%",
        overflowX: "auto",
        "&::-webkit-scrollbar": { height: 15 },
        "&::-webkit-scrollbar-track": { bgcolor: "grey.100" },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "grey.400",
          borderRadius: "7.5px",
          border: "4px solid",
          borderColor: "grey.100",
        },
      }}
    >
      {/* Paper (same elevation as Card) instead of a plain background.paper
          Box: in dark mode paper and default are both #121212, so only the
          elevation overlay keeps the container visible. */}
      <Paper
        role="tablist"
        sx={{
          display: "flex",
          gap: 2,
          minWidth: "fit-content",
          p: 1,
          borderRadius: 4,
        }}
      >
        {products.map((product) => {
          const selected = product.type === selectedType;
          return (
            <ButtonBase
              key={product.type}
              role="tab"
              aria-selected={selected}
              data-testid={`product-tab-${product.type}`}
              onClick={() => onSelect(product.type)}
              sx={(theme) => ({
                flex: "1 1 0",
                minWidth: 190,
                p: 2,
                borderRadius: 4,
                border: 1,
                borderColor: "divider",
                bgcolor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                "&:hover": selected
                  ? undefined
                  : { bgcolor: theme.palette.action.hover },
              })}
            >
              <ProductTabStatus product={product} />
              <Stack direction="row" spacing={1} alignItems="center">
                <img
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  alt={product.title}
                  src={product.icon}
                  width={20}
                  height={20}
                />
                <Typography variant="body1" fontWeight={500} noWrap>
                  {product.title}
                </Typography>
              </Stack>
            </ButtonBase>
          );
        })}
      </Paper>
    </Box>
  );
}
